import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { BarChart3, Building2, CalendarDays, Check, CheckSquare, ChevronsUpDown, Grid3x3, LayoutDashboard, ListChecks, LogOut, Mail, Plus, Radio, Repeat, Settings, Sparkles, Users } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import {
  Alert,
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ErrorState,
  LoadingState,
  ThemeToggle,
  TopNavLayout,
  type SidebarNavItem,
} from '@marketingbee/ui';
import { ROLE_LABELS } from '@marketingbee/contracts';
import { useCurrent } from '../components/common';
import { authClient } from '../lib/auth-client';
import { trpc } from '../lib/trpc';
import { setWorkspaceSlug, useWorkspaceSlug } from '../lib/workspace';
import { Wordmark } from './Logo';

type NavDef = { key: string; label: string; to: string; icon: ReactNode; match: (p: string) => boolean };

const NAV: NavDef[] = [
  { key: 'home', label: 'Dashboard', to: '/dashboard', icon: <LayoutDashboard />, match: (p) => p === '/dashboard' },
  { key: 'calendar', label: 'Calendar', to: '/calendar', icon: <CalendarDays />, match: (p) => p.startsWith('/calendar') || p.startsWith('/content') },
  { key: 'review', label: 'Review', to: '/review', icon: <CheckSquare />, match: (p) => p.startsWith('/review') },
  { key: 'brand', label: 'Brand', to: '/brand', icon: <Sparkles />, match: (p) => p.startsWith('/brand') },
  { key: 'channels', label: 'Channels', to: '/channels', icon: <Radio />, match: (p) => p.startsWith('/channels') },
  { key: 'results', label: 'Results', to: '/results', icon: <BarChart3 />, match: (p) => p.startsWith('/results') },
  { key: 'email', label: 'Email', to: '/email', icon: <Mail />, match: (p) => p.startsWith('/email') },
  { key: 'crm', label: 'Contacts', to: '/crm/contacts', icon: <Users />, match: (p) => p.startsWith('/crm') },
  { key: 'sequences', label: 'Sequences', to: '/sequences', icon: <Repeat />, match: (p) => p.startsWith('/sequences') },
  { key: 'settings', label: 'Settings', to: '/settings', icon: <Settings />, match: (p) => p.startsWith('/settings') },
];

/** Signed-in shell: resolves the active workspace, then renders the top bar and the page. */
export function AppLayout() {
  const me = trpc.workspace.me.get.useQuery();
  const slug = useWorkspaceSlug();
  const navigate = useNavigate();
  const workspaces = me.data?.workspaces;
  useEffect(() => {
    if (!workspaces) return;
    if (workspaces.length === 0) {
      void navigate({ to: '/onboarding' });
      return;
    }
    if (!slug || !workspaces.some((w) => w.slug === slug)) {
      const preferred = workspaces.find((w) => w.id === me.data?.lastWorkspaceId) ?? workspaces[0]!;
      setWorkspaceSlug(preferred.slug);
    }
  }, [workspaces, slug, me.data?.lastWorkspaceId, navigate]);

  if (me.isLoading) return <LoadingState label="Loading your workspace" className="min-h-dvh" />;
  if (me.error) return <ErrorState className="m-8" error={me.error} onRetry={() => void me.refetch()} />;
  const active = workspaces?.find((w) => w.slug === slug);
  if (!active) return <LoadingState label="Opening workspace" className="min-h-dvh" />;
  return <Shell key={active.id} />;
}

function PlanBanner() {
  const current = useCurrent();
  const ent = current.data?.entitlements;
  if (!ent) return null;
  if (!ent.active) {
    return (
      <Alert variant="destructive" className="mb-6" title="This workspace is read-only">
        {ent.state === 'suspended' ? 'It has been suspended.' : 'The trial or subscription has ended.'} Your data is kept and can be exported.{' '}
        {current.data?.hiveManaged ? (current.data.hive ? <a className="font-semibold underline" href={current.data.hive.billingUrl}>Manage in Hive</a> : 'Manage the plan in Hive.') : <Link className="font-semibold underline" to="/settings/billing">Choose a plan</Link>}
      </Alert>
    );
  }
  if (ent.state === 'trialing' && ent.trialDaysLeft !== null && !current.data?.hiveManaged) {
    return (
      <Alert variant="info" className="mb-6">
        {ent.planName} trial: {ent.trialDaysLeft} day{ent.trialDaysLeft === 1 ? '' : 's'} left.{' '}
        <Link className="font-semibold underline" to="/settings/billing">Choose a plan</Link>
      </Alert>
    );
  }
  if (ent.state === 'past_due') return <Alert variant="warning" className="mb-6">Payment failed: update the card to keep publishing.</Alert>;
  return null;
}

function Shell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = useCurrent();
  const nav: SidebarNavItem[] = NAV.map((item) => ({
    key: item.key,
    label: item.label,
    icon: item.icon,
    active: item.match(pathname),
    render: ({ className, children, onNavigate }) => (
      <Link to={item.to} className={className} onClick={onNavigate}>
        {children}
      </Link>
    ),
  }));
  const appsUrl = current.data?.hive?.appsUrl;
  return (
    <TopNavLayout
      brand={
        <div className="flex min-w-0 items-center gap-3">
          <Link to="/dashboard" aria-label="MarketingBee home" className="shrink-0">
            <Wordmark className="text-[17px] [&_svg]:size-7" />
          </Link>
          <span aria-hidden className="hidden h-6 w-px shrink-0 bg-border sm:block" />
          <WorkspaceSwitcher />
        </div>
      }
      nav={nav}
      actions={
        <>
          {current.data?.hiveManaged && appsUrl ? (
            <Button asChild variant="ghost" size="sm">
              <a href={appsUrl} title="All WRKRBZ apps">
                <Grid3x3 /> All apps
              </a>
            </Button>
          ) : null}
          <ThemeToggle />
          <UserMenu />
        </>
      }
    >
      <PlanBanner />
      <Outlet />
    </TopNavLayout>
  );
}

function WorkspaceSwitcher() {
  const me = trpc.workspace.me.get.useQuery();
  const slug = useWorkspaceSlug();
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const setLast = trpc.workspace.me.setLastWorkspace.useMutation();
  const navigate = useNavigate();
  const current = me.data?.workspaces.find((w) => w.slug === slug);
  const switchTo = async (w: { id: string; slug: string }) => {
    if (w.slug === slug) return;
    setWorkspaceSlug(w.slug);
    setLast.mutate({ workspaceId: w.id });
    queryClient.removeQueries({ predicate: (q) => !JSON.stringify(q.queryKey).includes('"me"') });
    await utils.invalidate();
    await navigate({ to: '/dashboard' });
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Switch workspace" className="flex max-w-60 items-center gap-2 rounded-lg py-1.5 pl-2 pr-2 text-left transition-colors hover:bg-foreground/[0.04]">
          <Building2 className="size-4 shrink-0 text-muted-foreground sm:hidden" />
          <span className="hidden min-w-0 truncate text-[15px] font-medium sm:block">{current?.name ?? 'Workspace'}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
        {me.data?.workspaces.map((w) => (
          <DropdownMenuItem key={w.id} icon={<Building2 />} onSelect={() => void switchTo(w)}>
            <span className="flex-1 truncate">{w.name}</span>
            <span className="text-xs text-muted-foreground">{ROLE_LABELS[w.role]}</span>
            {w.slug === slug ? <Check /> : null}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<Plus />} onSelect={() => void navigate({ to: '/onboarding' })}>
          Create workspace
        </DropdownMenuItem>
        <DropdownMenuItem icon={<ListChecks />} onSelect={() => void navigate({ to: '/onboarding' })}>
          Setup checklist
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu() {
  const me = trpc.workspace.me.get.useQuery();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = me.data?.user;
  if (!user) return null;
  const signOut = async () => {
    await authClient.signOut();
    setWorkspaceSlug(null);
    queryClient.clear();
    await navigate({ to: '/sign-in' });
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={`Account: ${user.name}`} className="rounded-full transition-opacity hover:opacity-85">
          <Avatar name={user.name} size="md" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="normal-case">
          <span className="block truncate text-[14px] font-semibold text-foreground">{user.name}</span>
          <span className="block truncate font-normal">{user.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<LogOut />} onSelect={() => void signOut()}>
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
